import AsyncStorage from '@react-native-async-storage/async-storage';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';

import { useAuth } from './AuthContext';
import { AreaUpdates, getUpdates } from '../api/userStories';

/**
 * Unread updates per area — CCT-348. Feeds the badges on Chat and Board.
 *
 * ⚠️ THERE WAS A BADGE HERE BEFORE, AND IT WAS A LIE. The menu carried `badge: 3`, written into
 * the configuration: three unread announced to everybody, for ever, whatever the conversations
 * held. It was removed rather than left lying — an invented number is worth less than no number.
 * This one counts, and what it counts is stated below.
 *
 * ⚠️ "UNREAD" NEEDS A REFERENCE, AND IT IS KEPT ON THE DEVICE. `lastSeen` is the moment the player
 * last OPENED each area; the server counts against it. A badge is a per-device convenience, not a
 * shared state: syncing it would need a table, a write on every screen opening, and a conflict
 * story — for a number that vanishes the moment it is looked at.
 *
 * ⚠️ NO MARKER MEANS NOTHING UNREAD. On a first run the badge stays empty instead of announcing
 * the whole history: a badge that starts wrong teaches the player to ignore it. It is therefore
 * armed at the FIRST refresh, and only counts what happens after that.
 */
export type UpdateArea = 'chat' | 'board';

interface UpdatesContextType extends AreaUpdates {
  refresh: () => Promise<void>;
  /** The player is looking at this area: what it held stops being unread. */
  markSeen: (area: UpdateArea) => void;
}

const UpdatesContext = createContext<UpdatesContextType | undefined>(undefined);

const KEY: Record<UpdateArea, string> = {
  chat: 'cc.seen.chat',
  board: 'cc.seen.board',
};

export function UpdatesProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth();
  const [counts, setCounts] = useState<AreaUpdates>({ chat: 0, board: 0 });
  const seen = useRef<Record<UpdateArea, string | null>>({ chat: null, board: null });
  const loaded = useRef(false);

  // The markers survive a reload: without that, every reload would rearm them at "now" and the
  // badge would only ever show what happened during the current session.
  useEffect(() => {
    void (async () => {
      try {
        const [c, b] = await Promise.all([AsyncStorage.getItem(KEY.chat),
                                          AsyncStorage.getItem(KEY.board)]);
        seen.current = { chat: c, board: b };
      } catch { /* no storage: the markers stay in memory for this session */ }
      loaded.current = true;
    })();
  }, []);

  const markSeen = useCallback((area: UpdateArea) => {
    const now = new Date().toISOString();
    seen.current = { ...seen.current, [area]: now };
    setCounts(cur => ({ ...cur, [area]: 0 }));   // the badge goes out at once, not at next refresh
    void AsyncStorage.setItem(KEY[area], now).catch(() => {});
  }, []);

  const refresh = useCallback(async () => {
    const token = getAccessToken();
    if (!user || !token) { setCounts({ chat: 0, board: 0 }); return; }
    // ⚠️ ARMING, ON THE FIRST REFRESH ONLY. Until a marker exists the server counts nothing; we
    // set it here rather than at mount so that a session opened and never looked at does not
    // silently swallow what arrived in between.
    (['chat', 'board'] as UpdateArea[]).forEach(area => {
      if (!seen.current[area]) markSeen(area);
    });
    try {
      setCounts(await getUpdates(token, seen.current.chat, seen.current.board));
    } catch {
      // A counter that cannot be fetched shows NOTHING. Keeping the previous value would announce
      // updates that may already have been read elsewhere.
      setCounts({ chat: 0, board: 0 });
    }
  }, [user, getAccessToken, markSeen]);

  // TWO CAUSES, AND NO MORE — owner's decision (CCT-391): one read when the session starts, and
  // one update when the ENGINE ANSWERS. No poll, and no refresh on window focus either.
  //
  // ⚠️ THE FOCUS LISTENER WAS THE DEFECT. Coming back to the tab fired a foreground request, so
  // the app announced "Syncing…" over a conversation nobody had touched — a counter speaking in
  // the player's name. And it fired on every return: switching windows, closing a dialog, opening
  // a file picker. `/me/notifications` polled every 45 s alone once kept the container app awake
  // 53 % of the time (CCT-233); a badge is not worth a heartbeat, nor a wake-up per glance.
  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    const nav: any = typeof navigator !== 'undefined' ? (navigator as any) : undefined;
    // The engine's answer arrives as a push: THAT is the moment a counter can have changed.
    const onSwMsg = (ev: MessageEvent) => {
      const d: any = ev.data || {};
      if (d.type === 'cc-push' || d.type === 'cc-push-open') void refresh();
    };
    nav?.serviceWorker?.addEventListener?.('message', onSwMsg);
    return () => { nav?.serviceWorker?.removeEventListener?.('message', onSwMsg); };
  }, [refresh]);

  return (
    <UpdatesContext.Provider value={{ ...counts, refresh, markSeen }}>
      {children}
    </UpdatesContext.Provider>
  );
}

export function useUpdates(): UpdatesContextType {
  const ctx = useContext(UpdatesContext);
  // No provider (a screen rendered outside the tree, a test): no badge, and no crash. A counter
  // is decoration on top of a working screen — it must never be what breaks it.
  return ctx ?? { chat: 0, board: 0, refresh: async () => {}, markSeen: () => {} };
}
